import React from 'react';
import { LanguageProvider } from './i18n';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useStore } from './store';
import { push as pushApi } from './services/api';
import { ThemeProvider, useTheme } from './theme';

import SplashScreen from './screens/SplashScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import HomeScreen from './screens/HomeScreen';
import WalletScreen from './screens/WalletScreen';
const TransferScreen = React.lazy(() => import('./screens/TransferScreen'));
const DBUXScreen = React.lazy(() => import('./screens/DBUXScreen'));
const QuantumNodeScreen = React.lazy(() => import('./screens/QuantumNodeScreen'));
const MindStakeScreen = React.lazy(() => import('./screens/MindStakeScreen'));
const KYCScreen = React.lazy(() => import('./screens/KYCScreen'));
import NotificationsScreen from './screens/NotificationsScreen';
const ModulesScreen = React.lazy(() => import('./screens/ModulesScreen'));
const VANTAChatScreen = React.lazy(() => import('./screens/VANTAChatScreen'));
import ProfileScreen from './screens/ProfileScreen';
import AuctionsScreen from './screens/AuctionsScreen';
import OrderHistoryScreen from './screens/OrderHistoryScreen';
import PulseScreen from './screens/PulseScreen';
import GAPScreen from './screens/GAPScreen';
import GAPProfileScreen from './screens/GAPProfileScreen';
import GAPEditScreen from './screens/GAPEditScreen';
const AdminScreen = React.lazy(() => import('./screens/AdminScreen'));
const ChatScreen = React.lazy(() => import('./screens/ChatScreen'));
const ChatListScreen = React.lazy(() => import('./screens/ChatListScreen'));
const DisputeScreen = React.lazy(() => import('./screens/DisputeScreen'));
const MyDisputesScreen = React.lazy(() => import('./screens/MyDisputesScreen'));
const PartnerScreen = React.lazy(() => import('./screens/PartnerScreen'));
const SSIStoreScreen = React.lazy(() => import('./screens/SSIStoreScreen'));
const TrainingScreen = React.lazy(() => import('./screens/TrainingScreen'));
const TrainingModuleScreen = React.lazy(() => import('./screens/TrainingModuleScreen'));
const CountryPartnerScreen = React.lazy(() => import('./screens/CountryPartnerScreen'));
const WorkScreen = React.lazy(() => import('./screens/WorkScreen'));
const TalentScreen = React.lazy(() => import('./screens/TalentScreen'));
const FindJobScreen = React.lazy(() => import('./screens/FindJobScreen'));
const FindWorkScreen = React.lazy(() => import('./screens/FindWorkScreen'));
const MatrimonyScreen = React.lazy(() => import('./screens/MatrimonyScreen'));
const ExploreTilesScreen = React.lazy(() => import('./screens/ExploreTilesScreen'));
const ExploreSectionsScreen = React.lazy(() => import('./screens/ExploreSectionsScreen'));
const BottomNav = React.lazy(() => import('./screens/BottomNav'));

const InboundScreen = React.lazy(() => import('./screens/MainScreens').then(m => ({ default: m.InboundScreen })));
const OutboundScreen = React.lazy(() => import('./screens/MainScreens').then(m => ({ default: m.OutboundScreen })));
const ServicesScreen = React.lazy(() => import('./screens/MainScreens').then(m => ({ default: m.ServicesScreen })));
const EarnScreen = React.lazy(() => import('./screens/EarnScreen'));
const TradingScreen = React.lazy(() => import('./screens/TradingScreen'));
const SubscriptionScreen = React.lazy(() => import('./screens/SubscriptionScreen'));
const MarketScreen = React.lazy(() => import('./screens/MarketScreen'));
const ShopScreen = React.lazy(() => import('./screens/ShopScreen'));
const FinanceScreen = React.lazy(() => import('./screens/FinanceScreen'));
const RentalsScreen = React.lazy(() => import('./screens/RentalsScreen'));
const StartupScreen = React.lazy(() => import('./screens/StartupScreen'));
const RolesScreen = React.lazy(() => import('./screens/RolesScreen'));
const RFQScreen = React.lazy(() => import('./screens/RFQScreen'));
const CategoryHubScreen = React.lazy(() => import('./screens/CategoryHubScreen'));
const HelloDoctorScreen = React.lazy(() => import('./screens/HelloDoctorScreen'));
const DoctorConsultationScreen = React.lazy(() => import('./screens/DoctorConsultationScreen'));
const PharmacyPortalScreen = React.lazy(() => import('./screens/PharmacyPortalScreen'));
const PortalScreen = React.lazy(() => import('./screens/PortalScreen'));
const GuidraScreen = React.lazy(() => import('./screens/GuidraScreen'));
const GuidraMeetingScreen = React.lazy(() => import('./screens/GuidraMeetingScreen'));
const GuidraPortalScreen = React.lazy(() => import('./screens/GuidraPortalScreen'));
const DeliveryPortalScreen = React.lazy(() => import('./screens/DeliveryPortalScreen'));
const DriverPortalScreen = React.lazy(() => import('./screens/DriverPortalScreen'));
const RideBookingScreen = React.lazy(() => import('./screens/RideBookingScreen'));
const MyBookingsScreen = React.lazy(() => import('./screens/MyBookingsScreen'));
const MyDealsScreen = React.lazy(() => import('./screens/MyDealsScreen'));
const DatingHubScreen = React.lazy(() => import('./screens/dating/DatingHubScreen'));
const DatingProfileEditScreen = React.lazy(() => import('./screens/dating/DatingProfileEditScreen'));
const DatingBrowseScreen = React.lazy(() => import('./screens/dating/DatingBrowseScreen'));
const DatingProfileViewScreen = React.lazy(() => import('./screens/dating/DatingProfileViewScreen'));
const DatingRequestsScreen = React.lazy(() => import('./screens/dating/DatingRequestsScreen'));
const MeetupHubScreen = React.lazy(() => import('./screens/meetup/MeetupHubScreen'));
const MeetupProfileEditScreen = React.lazy(() => import('./screens/meetup/MeetupProfileEditScreen'));
const MeetupBrowseScreen = React.lazy(() => import('./screens/meetup/MeetupBrowseScreen'));
const MeetupProfileViewScreen = React.lazy(() => import('./screens/meetup/MeetupProfileViewScreen'));
const MeetupRequestsScreen = React.lazy(() => import('./screens/meetup/MeetupRequestsScreen'));
const MeetupChatListScreen = React.lazy(() => import('./screens/meetup/MeetupChatListScreen'));
const MeetupChatScreen     = React.lazy(() => import('./screens/meetup/MeetupChatScreen'));
const CategoryLandingScreen = React.lazy(() => import('./screens/CategoryLandingScreen'));
const ModuleScreen = React.lazy(() => import('./screens/ModuleScreen'));
const ProviderDashboard = React.lazy(() => import('./screens/ProviderDashboard'));
const ConsumerScreen    = React.lazy(() => import('./screens/ConsumerScreen'));
const QuickHireWorkerDashboard = React.lazy(() => import('./screens/QuickHireWorkerDashboard'));
const ExchangerDashboard = React.lazy(() => import('./screens/ExchangerDashboard'));
const TxcAgreementScreen = React.lazy(() => import('./screens/TxcAgreementScreen'));
const ProviderSettings = React.lazy(() => import('./screens/ProviderSettings'));
const BrowseRouter = React.lazy(() => Promise.resolve({
  default: function BrowseRouter() {
    const { id } = require('react-router-dom').useParams();
    const nav = require('react-router-dom').useNavigate();
    React.useEffect(()=>{
      if(id && id.startsWith('mod_')) return;
    },[id]);
    if(id && id.startsWith('mod_')) {
      const ModScreen = React.lazy(() => import('./screens/ModuleScreen'));
      return React.createElement(React.Suspense, {fallback:React.createElement('div',null,'Loading...')},
        React.createElement(ModScreen));
    }
    const CatScreen = React.lazy(() => import('./screens/CategoryLandingScreen'));
    return React.createElement(React.Suspense, {fallback:React.createElement('div',null,'Loading...')},
      React.createElement(CatScreen));
  }
}));
const CategoryLeafScreen = React.lazy(() => import('./screens/CategoryLeafScreen'));
const QuickOrderScreen = React.lazy(() => import('./screens/QuickOrderScreen'));
const MyEarnsScreen = React.lazy(() => import('./screens/MyEarnsScreen'));
const TermsScreen = React.lazy(() => import('./screens/legal/TermsScreen'));
const PrivacyScreen = React.lazy(() => import('./screens/legal/PrivacyScreen'));
const ExploreScreen = React.lazy(() => import('./screens/ExploreScreen'));

const OrderScreen = React.lazy(() => import('./screens/OrderScreen'));
const MarketplaceScreen = React.lazy(() => import('./screens/MarketplaceScreen'));


class ErrorBoundary extends React.Component {
  constructor(props) { 
    super(props); 
    this.state = { hasError: false, error: null, errorInfo: null, errorCount: 0 }; 
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { 
    console.error('[ErrorBoundary]', error, info);
    this.setState(s => ({ errorInfo: info, errorCount: s.errorCount + 1 }));
    // Log to backend for monitoring
    try {
      const payload = {
        message: error?.message || 'Unknown error',
        stack: error?.stack || '',
        component: info?.componentStack || '',
        url: window.location.href,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
      fetch('/api/backend/v1/log/error', {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch {}
  }
  reset = () => { this.setState({ hasError:false, error:null, errorInfo:null }); }
  render() {
    if (this.state.hasError) {
      const isDark = document.body.classList.contains('dark');
      const bg = isDark ? '#0f0f1e' : '#f5f6fa';
      const cardBg = isDark ? '#1a1a2e' : '#fff';
      const textColor = isDark ? '#fff' : '#1a1a2e';
      const mutedColor = isDark ? '#9ca3af' : '#666';
      
      return (
        <div style={{padding:'40px 20px',textAlign:'center',background:bg,minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:cardBg,padding:'32px 24px',borderRadius:20,maxWidth:400,width:'100%',boxShadow:'0 10px 30px rgba(0,0,0,0.1)'}}>
            <div style={{fontSize:56,marginBottom:16}}>⚠️</div>
            <div style={{fontSize:18,fontWeight:700,marginBottom:8,color:textColor}}>Something went wrong</div>
            <div style={{fontSize:13,color:mutedColor,marginBottom:16}}>
              {this.state.error?.message||'An unexpected error occurred'}
            </div>
            {this.state.errorCount > 2 && (
              <div style={{fontSize:11,color:'#f97316',marginBottom:16,padding:'8px 12px',background:'#f9731612',borderRadius:8}}>
                This error keeps happening. Try refreshing the page or going home.
              </div>
            )}
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <button onClick={this.reset}
                style={{background:'#1a3cc8',color:'#fff',border:'none',borderRadius:12,padding:'12px 24px',fontSize:14,fontWeight:700,cursor:'pointer'}}>
                🔄 Try Again
              </button>
              <button onClick={()=>{this.reset();window.history.back();}}
                style={{background:'transparent',color:'#1a3cc8',border:`1px solid ${isDark?'#1a3cc8':'#1a3cc8'}`,borderRadius:12,padding:'10px 24px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                ← Go Back
              </button>
              <button onClick={()=>{this.reset();window.location.href='/home';}}
                style={{background:'transparent',color:mutedColor,border:'none',padding:'8px 24px',fontSize:12,fontWeight:500,cursor:'pointer',textDecoration:'underline'}}>
                🏠 Go Home
              </button>
            </div>
            {this.state.errorInfo && (
              <details style={{marginTop:20,textAlign:'left',fontSize:10,color:mutedColor}}>
                <summary style={{cursor:'pointer',fontSize:11}}>Technical details</summary>
                <pre style={{fontSize:9,overflow:'auto',maxHeight:100,padding:8,background:isDark?'#0f0f1e':'#f5f6fa',borderRadius:6,marginTop:6}}>
                  {this.state.error?.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const isDark = useStore(s => s.isDark);
  return (
    <LanguageProvider>
      <ThemeProvider isDark={isDark}>
      <BrowserRouter>
        <a href="#main-content" style={{
          position:'absolute',top:-40,left:0,background:'#1a3cc8',color:'#fff',
          padding:'8px 16px',textDecoration:'none',zIndex:100000,borderRadius:'0 0 8px 0',
          fontSize:13,fontWeight:600,transition:'top 0.2s',
        }}
        onFocus={e=>e.target.style.top='0'}
        onBlur={e=>e.target.style.top='-40px'}
        >Skip to main content</a>
        <Toaster
          position="top-center"
          toastOptions={{
            style:{background:'#1a3cc8',color:'#fff',border:'none',borderRadius:12},
            duration:4000
          }}
        />
        <ErrorBoundary><AppRoutes /></ErrorBoundary>
      </BrowserRouter>
    </ThemeProvider>
      </LanguageProvider>
  );
}

function AppRoutes() {
  const C = useTheme();
  const token = useStore(s => s.token);
  const user = useStore(s => s.user);
  const setBalance = useStore(s => s.setBalance);
  
  // ═══ GLOBAL BALANCE FETCHER ═══
  React.useEffect(() => {
    if (!token || !user?.id) return;
    // Auto-subscribe to push notifications
    if(typeof Notification !== 'undefined' && Notification.permission === 'default') {
      setTimeout(()=>pushApi.subscribe().catch(()=>{}), 3000);
    }
    let cancelled = false;
    const fetchBalance = async () => {
      try {
        const { wallet } = await import("./services/api");
        const b = await wallet.balance(user.id);
        if (!cancelled) {
          setBalance({ txc: b?.txc_balance || 0, vgc: b?.vgc_balance || 0 });
        }
      } catch (e) { console.error("[balance] fetch failed:", e); }
    };
    fetchBalance();
    const interval = setInterval(fetchBalance, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [token, user?.id, setBalance]);
  

  const Protected = ({ children }) =>
    token ? children : <Navigate to="/login" replace />;

  const Layout = ({ children }) => {
    const [pullY, setPullY] = React.useState(0);
    const [refreshing, setRefreshing] = React.useState(false);
    const startY = React.useRef(0);
    const pulling = React.useRef(false);
    const scrollRef = React.useRef(null);
    const location = useLocation();

    // Save & restore scroll position for all routes
    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const key = 'scroll_' + location.pathname.replace(/\//g,'_');
      const saved = sessionStorage.getItem(key);
      if (saved) {
        const tryRestore = (attempts) => {
          if (!el) return;
          el.scrollTop = parseInt(saved);
          if (el.scrollTop < parseInt(saved) - 10 && attempts > 0) {
            setTimeout(() => tryRestore(attempts - 1), 100);
          }
        };
        setTimeout(() => tryRestore(8), 80);
      } else {
        el.scrollTop = 0;
      }
    }, [location.pathname]);

    // Save scroll position for current route on scroll
    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const key = 'scroll_' + location.pathname.replace(/\//g,'_');
      const onScroll = () => sessionStorage.setItem(key, el.scrollTop);
      el.addEventListener('scroll', onScroll);
      return () => el.removeEventListener('scroll', onScroll);
    }, [location.pathname]);

    const onTouchStart = (e) => {
      const el = scrollRef.current;
      if (el && el.scrollTop === 0) {
        startY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    };
    const onTouchMove = (e) => {
      if (!pulling.current) return;
      const el = scrollRef.current;
      if (!el || el.scrollTop > 0) { pulling.current = false; setPullY(0); return; }
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0) setPullY(Math.min(dy * 0.4, 80));
    };
    const onTouchEnd = () => {
      if (pullY > 50) {
        setRefreshing(true);
        setTimeout(() => window.location.reload(), 800);
      } else {
        setPullY(0);
      }
      pulling.current = false;
    };

    return (
      <div style={{
        position:'fixed', top:0, bottom:0,
        left:'50%', marginLeft:'-215px',
        width:'100%', maxWidth:430, height:'100%',
        display:'flex', flexDirection:'column',
        background:C.bg, overflow:'hidden',
      }}>
        {/* Status bar safe area — FIXED overlay, always on top */}
        <div style={{
          position:'fixed',
          top:0, left:0, right:0,
          height:'env(safe-area-inset-top, 0px)',
          background:'#ffffff',
          zIndex:99999,
          pointerEvents:'none',
        }}/>

        {/* Pull to refresh */}
        <div style={{
          flexShrink:0, overflow:'hidden',
          height: refreshing ? 44 : pullY,
          display:'flex', alignItems:'center', justifyContent:'center',
          background:C.bg,
          transition: pulling.current ? 'none' : 'height 0.25s ease',
        }}>
          {refreshing
            ? <div style={{width:22,height:22,border:`3px solid ${C.border}`,borderTop:`3px solid ${C.primary}`,borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
            : <div style={{fontSize:12,color:C.muted,fontWeight:600}}>{pullY>50?'↑ Release to refresh':'↓ Pull to refresh'}</div>
          }
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef} data-scroll="true"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={{
            flex:1, overflowY:'auto', overflowX:'hidden',
            WebkitOverflowScrolling:'touch',
            background:C.bg,
          }}
        >
          {children}
          <div style={{height:'calc(env(safe-area-inset-bottom,0px) + 20px)'}}/>
        </div>

        {/* BottomNav */}
        <BottomNav />
      </div>
    );
  };


  const P = ({ children }) => (
    <Protected><Layout>{children}</Layout></Protected>
  );

  return (
    <main id="main-content" role="main">
          <React.Suspense fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"80vh"}}><div style={{width:32,height:32,border:"3px solid #e5e5e5",borderTop:"3px solid #1a3cc8",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/></div>}>
          <Routes>
      <Route path="/"                     element={<SplashScreen />} />
      <Route path="/login"                element={<LoginScreen />} />
      <Route path="/register"             element={<RegisterScreen />} />
      <Route path="/forgot-password"      element={<ForgotPasswordScreen />} />
      <Route path="/onboarding"           element={<P><OnboardingScreen /></P>} />
      <Route path="/home"                 element={<P><HomeScreen /></P>} />
      <Route path="/wallet"               element={<P><WalletScreen /></P>} />
      <Route path="/transfer"             element={<P><TransferScreen /></P>} />
      <Route path="/inbound"              element={<P><InboundScreen /></P>} />
      <Route path="/outbound"             element={<P><OutboundScreen /></P>} />
      <Route path="/services"             element={<Navigate to="/explore-sections" replace/>} />
      <Route path="/startups"             element={<Navigate to="/explore-sections" replace/>} />
      <Route path="/dbux"                 element={<P><DBUXScreen /></P>} />
      <Route path="/quantum-node"         element={<P><QuantumNodeScreen /></P>} />
      <Route path="/mindstake"            element={<P><MindStakeScreen /></P>} />
      <Route path="/marketplace"          element={<P><MarketplaceScreen /></P>} />
          <Route path="/auctions"             element={<P><AuctionsScreen /></P>} />
          <Route path="/work"                 element={<P><WorkScreen /></P>} />
          <Route path="/talent/jobs"          element={<P><TalentScreen /></P>} />
          <Route path="/find-job"             element={<P><FindJobScreen /></P>} />
          <Route path="/find-work"            element={<P><FindWorkScreen /></P>} />
          <Route path="/matrimony"            element={<P><MatrimonyScreen /></P>} />
          <Route path="/explore-tiles"        element={<Navigate to="/explore-sections" replace/>} />
          <Route path="/explore-sections"     element={<P><ExploreSectionsScreen /></P>} />
      <Route path="/order"                element={<P><OrderScreen /></P>} />
      <Route path="/profile"              element={<P><ProfileScreen /></P>} />
          <Route path="/admin"                element={<P><AdminScreen /></P>} />
          <Route path="/chat"                 element={<P><ChatListScreen /></P>} />
          <Route path="/chat/:threadId"       element={<P><ChatScreen /></P>} />
          <Route path="/chat/order/:orderId"  element={<P><ChatScreen /></P>} />
          <Route path="/disputes"             element={<P><MyDisputesScreen /></P>} />
          <Route path="/disputes/:id"         element={<P><DisputeScreen /></P>} />
          <Route path="/partner"              element={<P><PartnerScreen /></P>} />
          <Route path="/store"                element={<P><SSIStoreScreen /></P>} />
          <Route path="/training"             element={<P><TrainingScreen /></P>} />
          <Route path="/training/:id"         element={<P><TrainingModuleScreen /></P>} />
          <Route path="/country-partner"      element={<P><CountryPartnerScreen /></P>} />
      <Route path="/kyc"                  element={<P><KYCScreen /></P>} />
      <Route path="/notifications"        element={<P><NotificationsScreen /></P>} />
      <Route path="/modules"              element={<Navigate to="/explore-sections" replace/>} />
      <Route path="/vanta"                element={<P><VANTAChatScreen /></P>} />
      <Route path="/roles"                element={<P><RolesScreen /></P>} />
      <Route path="/rfq"                  element={<P><RFQScreen /></P>} />
      <Route path="/rfq/:rfqId"           element={<P><RFQScreen /></P>} />
      <Route path="/cat/:categoryId"      element={<P><CategoryHubScreen /></P>} />
      <Route path="/my-bookings"         element={<P><MyBookingsScreen /></P>} />
      <Route path="/my-deals"            element={<P><MyDealsScreen /></P>} />
      <Route path="/dating"               element={<P><DatingHubScreen /></P>} />
      <Route path="/dating/profile/edit"  element={<P><DatingProfileEditScreen /></P>} />
      <Route path="/dating/browse"        element={<P><DatingBrowseScreen /></P>} />
      <Route path="/dating/profile/:id"   element={<P><DatingProfileViewScreen /></P>} />
      <Route path="/dating/requests"      element={<P><DatingRequestsScreen /></P>} />
      <Route path="/meetup"                element={<Navigate to="/consumer/mod_meetup" replace />} />
      <Route path="/meetup/profile/edit"    element={<P><MeetupProfileEditScreen /></P>} />
      <Route path="/meetup/profile/:userId" element={<P><MeetupProfileViewScreen /></P>} />
      <Route path="/meetup/browse"          element={<P><MeetupBrowseScreen /></P>} />
      <Route path="/meetup/requests"        element={<P><MeetupRequestsScreen /></P>} />
      <Route path="/meetup/chats"           element={<P><MeetupChatListScreen /></P>} />
      <Route path="/meetup/chats/:chatId"   element={<P><MeetupChatScreen /></P>} />
      <Route path="/browse"               element={<P><CategoryLandingScreen /></P>} />
      <Route path="/browse/mod_pulse"     element={<Navigate to="/consumer/mod_pulse" replace />} />
      <Route path="/browse/mod_gap"        element={<Navigate to="/gap"    replace />} />
      <Route path="/browse/mod_meetup"     element={<Navigate to="/consumer/mod_meetup" replace />} />
      <Route path="/browse/:id"           element={<P><BrowseRouter /></P>} />
      <Route path="/hub/:hubId" element={<P><CategoryHubScreen /></P>} />
      <Route path="/hello-doctor" element={<P><HelloDoctorScreen /></P>} />
      <Route path="/doctor-consultation" element={<P><DoctorConsultationScreen /></P>} />
      <Route path="/pharmacy-portal" element={<P><PharmacyPortalScreen /></P>} />
      <Route path="/guidra" element={<P><GuidraScreen /></P>} />
      <Route path="/guidra/meeting/:sessionId/:meetingId" element={<P><GuidraMeetingScreen /></P>} />
      <Route path="/guidra-portal" element={<P><GuidraPortalScreen /></P>} />
      <Route path="/delivery-portal" element={<P><DeliveryPortalScreen /></P>} />
      <Route path="/driver-portal" element={<P><DriverPortalScreen /></P>} />
      <Route path="/ride" element={<P><RideBookingScreen /></P>} />
      <Route path="/ride" element={<P><RideBookingScreen /></P>} />
      <Route path="/driver-portal" element={<P><DriverPortalScreen /></P>} />
      <Route path="/ride" element={<P><RideBookingScreen /></P>} />
      <Route path="/ride" element={<P><RideBookingScreen /></P>} />
          <Route path="/consumer/:moduleId"       element={<P><ConsumerScreen /></P>} />
          <Route path="/provider/mod_quick_hire" element={<P><QuickHireWorkerDashboard /></P>} />
          <Route path="/provider/:roleId"       element={<P><ProviderDashboard /></P>} />
          <Route path="/exchanger"               element={<P><ExchangerDashboard /></P>} />
          <Route path="/txc-agreement"           element={<P><TxcAgreementScreen /></P>} />
          <Route path="/provider/:roleId/settings" element={<P><ProviderSettings /></P>} />
      <Route path="/leaf/:id"             element={<P><CategoryLeafScreen /></P>} />
      <Route path="/quick/:category"      element={<P><QuickOrderScreen /></P>} />
      <Route path="/my-earns"             element={<P><MyEarnsScreen /></P>} />
      <Route path="/terms"                element={<P><TermsScreen /></P>} />
      <Route path="/privacy"              element={<P><PrivacyScreen /></P>} />
      <Route path="/startup/:id"          element={<P><StartupScreen /></P>} />
      <Route path="/subscribe"            element={<P><SubscriptionScreen /></P>} />
      <Route path="/subscribe/:id"        element={<P><SubscriptionScreen /></P>} />
      <Route path="/trade"                element={<P><TradingScreen /></P>} />
      <Route path="/earn"                 element={<P><EarnScreen /></P>} />
      <Route path="/earn/quiz"            element={<P><MindStakeScreen /></P>} />
      <Route path="/earn/streak"          element={<P><EarnScreen /></P>} />
      <Route path="/earn/scratch"         element={<P><EarnScreen /></P>} />
      <Route path="/earn/refer"           element={<P><EarnScreen /></P>} />
      <Route path="/earn/tournament"      element={<P><EarnScreen /></P>} />
      <Route path="/earn/leaderboard"     element={<P><EarnScreen /></P>} />
      <Route path="/market"               element={<P><MarketScreen /></P>} />
      <Route path="/shop"                 element={<P><ShopScreen /></P>} />
      <Route path="/finance"              element={<P><FinanceScreen /></P>} />
      <Route path="/rentals"              element={<P><RentalsScreen /></P>} />
      <Route path="/order-history"       element={<P><OrderHistoryScreen /></P>} />
      <Route path="/pulse" element={<Navigate to="/consumer/mod_pulse" replace />} />
      <Route path="/gap"                   element={<P><GAPScreen /></P>} />
      <Route path="/gap/edit"              element={<P><GAPEditScreen /></P>} />
      <Route path="/gap/profile/:id"      element={<P><GAPProfileScreen /></P>} />
      <Route path="/portal"                  element={<PortalScreen />} />
      <Route path="*"                     element={<Navigate to="/home" replace />} />
    </Routes>
          </React.Suspense>
        </main>
  );
}
